import { routes } from "../api/routes";

// Client calls for the appointments flow (plan §6). Deliberately separate from
// reservation.service.js: the restaurant flow is party-size driven, this one is
// service x resource driven, and §5.3 asks for the API layer to be forked rather
// than parameterised.

const jsonOrThrow = async response => {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || "Something went wrong");
  }
  return response.json();
};

const get = async url => {
  try {
    const response = await fetch(url, { method: "GET", headers: { Accept: "application/json" } });
    return await jsonOrThrow(response);
  } catch (error) {
    console.error("error calling", url, error);
    throw error;
  }
};

const post = async (url, data) => {
  try {
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { Accept: "application/json", "Content-Type": "application/json" }
    });
    return await jsonOrThrow(response);
  } catch (error) {
    console.error("error calling", url, error);
    throw error;
  }
};

/**
 * `resourceId` is omitted for "any" — the server then matches a resource per slot.
 * Either id may be absent: a business that has defined only services, or only
 * resources, books with just the one it has. The server requires at least one.
 */
const slotsQuery = ({ businessId, date, serviceId, resourceId }) => {
  const params = new URLSearchParams({ businessId, date });
  if (serviceId) {
    params.set("serviceId", serviceId);
  }
  if (resourceId && resourceId !== "any") {
    params.set("resourceId", resourceId);
  }
  return params;
};

/** One day's slot list. */
export async function getAppointmentSlots(params) {
  return get(`${routes.getAppointmentAvailableSlots}?${slotsQuery(params)}`);
}

/** Earliest bookable slot at or after `date`, scanning to the end of the window. */
export async function getFirstAvailableAppointment(params) {
  const query = slotsQuery(params);
  query.set("firstAvailable", "true");
  return get(`${routes.getAppointmentAvailableSlots}?${query}`);
}

/**
 * `{ date, closed }` for every day of the booking window. Only the server can answer
 * this — per-resource schedules and shift overrides never reach the client.
 */
export async function getAppointmentDays(params) {
  return get(`${routes.getAppointmentDays}?${slotsQuery(params)}`);
}

export async function createAppointmentWithPendingStatus(data) {
  return post(routes.createAppointmentWithPendingStatus, data);
}

export async function confirmAppointment(data) {
  return post(routes.confirmAppointment, data);
}

export async function editAppointment(data) {
  return post(routes.editAppointment, data);
}
